import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ApolloError } from '@apollo/client/core';
import { AngularTokenService, SignInData } from '@raysuelzer/angular-token';
import { flatMap as _flatMap, uniq as _uniq } from 'lodash-es';
import { finalize, map, mergeMap } from 'rxjs/operators';

import { AcceptInvitationInput, AcceptInviteGQL, UserDetailsFragment } from 'generated/graphql';

import { TermsDialogComponent } from 'auth/dialogs/terms-dialog/terms-dialog.component';

import { e164PhoneValidator, mustMatch, validationToErrorMessages } from '../../../core/validators';

// This object contains error messages which correspond with the form
// these are the error messages the correspond to the errors on the
// form control object.
const formErrorMessages = {
  firstName: {
    required: 'First name is required.'
  },
  lastName: {
    required: 'Last name is required.'
  },
  email: {
    required: 'Email address is required.'
  },
  campaigns: {
    required: 'Campaigns are required'
  },
  acceptedTOS: {
    requiredTrue: 'You must accept the Terms and Conditions.',
    required: 'You must accept the Terms and Conditions.'
  },
  password: {
    required: 'A password is required',
    mustMatch: 'Your passwords must match.',
    minlength: 'Password must be at least 8 characters.'
  },
  passwordConfirmation: {
    required: 'A password is required',
    mustMatch: 'Your passwords must match.',
    minlength: 'Password must be at least 8 characters.'
  }
};

@Component({
  selector: 'app-accept-invitation',
  templateUrl: './accept-invitation.component.html'
})
export class AcceptInvitationComponent implements OnInit {
  // Invitation Token
  invitationToken: string;
  // Reactive Form
  acceptInviteForm: UntypedFormGroup;
  // disables submit button
  isSubmitting = false;
  // Error messages
  errorMessages: Array<string> = [];
  // Sign in data, to log user in after registered
  signInData: SignInData = {} as SignInData;
  // Is pw displayed
  pwShown = false;

  constructor(
    private route: ActivatedRoute,
    private acceptInviteGQL: AcceptInviteGQL,
    private tokenServce: AngularTokenService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private dialog: MatDialog
  ) {

    this.acceptInviteForm = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [{ value: '', disabled: true }, Validators.email],
      phoneNumber: ['', e164PhoneValidator],
      password: ['', [Validators.required, Validators.minLength(8)]],
      passwordConfirmation: ['', [Validators.required, Validators.minLength(8), mustMatch('password')]],
      acceptedTOS: [false, Validators.requiredTrue]
    });

    this.acceptInviteForm.get('password').valueChanges.subscribe(() =>
      this.acceptInviteForm.get('passwordConfirmation').updateValueAndValidity()
    );

  }

  // -- PUBLIC METHODS -- //
  onShowTermsClick() {
    this.dialog.open(TermsDialogComponent);
  }

  // Called when submit button is clicked.
  onSubmit() {
    this.isSubmitting = true;
    this.errorMessages = _uniq(validationToErrorMessages(this.acceptInviteForm, formErrorMessages));

    if (this.acceptInviteForm.invalid) {
      this.isSubmitting = false;
      return;
    }

    const input = this.prepareAcceptInvite();

    // Send mutation to server
    this.acceptInviteGQL.mutate({ input }).pipe(
      // Log in
      mergeMap(r => {
        this.signInData.login = this.acceptInviteForm.controls.email.value;
        // ^ note that because the email field is disabled it is not included in acceptInviteForm.value
        this.signInData.password = this.acceptInviteForm.value.password;
        return this.tokenServce.signIn(this.signInData);
      }),
      finalize(() => this.isSubmitting = false))
      .subscribe((res) => this.router.navigateByUrl('/login'),
        (err: ApolloError) => {
          // Handle errors
          this.isSubmitting = false;
          // Flatten errors into array of error message strings
          // and display to user.
          this.errorMessages = _flatMap(err.graphQLErrors, e => e.message);
        },

        // Clean up
        () => { /*Done*/ }
      );
  }

  // Converts form to GQL Input
  prepareAcceptInvite(): AcceptInvitationInput {
    const formValue = this.acceptInviteForm.value;

    const acceptInviteModel = {
      invitationToken: this.invitationToken,
      password: formValue.password,
      passwordConfirmation: formValue.passwordConfirmation,
      updatedUserData: {
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        phoneNumber: formValue.phoneNumber
      }
    };

    return acceptInviteModel;
  }

  // -- Lifecycle hooks -- //

  // User data should have been fetched by the resesolver before this route
  // is activated. This retrieves this data from the router.
  // See: accept-inivitation-resolver
  ngOnInit() {
    this.route.data.pipe(
      map(d => d['userInviteData'] as { user: UserDetailsFragment; token: string }))
      .subscribe((data) => {
        this.invitationToken = data.token;
        this.acceptInviteForm.patchValue({
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          email: data.user.email,
          phoneNumber: data.user.phoneNumber,
          password: '',
          passwordConfirmation: '',
          acceptedTOS: false
        });
      });
  }
}
